import React, { useState, useEffect } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Typography,
  SelectChangeEvent,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import CancelIcon from "@mui/icons-material/Cancel";

import { API_IBGE } from "../../config/apiIBGE";
import { API_URLS } from "../../config/apiUrls";

interface StateIBGE {
  id: number;
  nome: string;
  sigla: string;
}

interface CityIBGE {
  nome: string;
  codigo_ibge: string;
}

interface AssignedCity {
  id: string; // O ID da cidade atribuída (codigo_ibge)
  name: string; // O nome da cidade atribuída
}

interface AtribuirCidadesContentProps {
  userId: number;
  token: string;
  onSave: (userId: number, cityIds: number[]) => void;
  onClose: () => void;
}

interface CitySelection {
  stateSigla: string;
  cityCodigoIBGE: string | null;
  cityNome: string | null;
}

const AtribuirCidades: React.FC<AtribuirCidadesContentProps> = ({
  userId,
  token,
  onSave,
  onClose,
}) => {
  const [states, setStates] = useState<StateIBGE[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [citySelections, setCitySelections] = useState<CitySelection[]>([
    { stateSigla: "", cityCodigoIBGE: null, cityNome: null },
  ]);
  const [citiesForSelection, setCitiesForSelection] = useState<CityIBGE[][]>([
    [],
  ]);
  const [assignedCitiesDetail, setAssignedCitiesDetail] = useState<
    AssignedCity[]
  >([]);
  const [stateIdToSiglaMap, setStateIdToSiglaMap] = useState<
    Map<string, string>
  >(new Map());

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "success" | "error" | "warning" | "info"
  >("info");
  const [loadingDelete, setLoadingDelete] = useState<boolean>(false);

  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
  const [cityToRemoveIdConfirm, setCityToRemoveIdConfirm] = useState<
    string | null
  >(null);
  const [cityToRemoveNameConfirm, setCityToRemoveNameConfirm] = useState<
    string | null
  >(null);

  // 1. Fetch States and build ID to Sigla Map
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await fetch(`${API_IBGE.estados}`);
        const data: StateIBGE[] = await response.json();
        const sortedStates = data.sort((a, b) => a.nome.localeCompare(b.nome));
        setStates(sortedStates);

        const idMap = new Map<string, string>();
        sortedStates.forEach((state) =>
          idMap.set(state.id.toString(), state.sigla)
        );
        setStateIdToSiglaMap(idMap);
      } catch (err) {
        setError("Erro ao carregar estados.");
        console.error("Erro ao carregar estados:", err);
      }
    };
    fetchStates();
  }, []);

  // Função para buscar as cidades atribuídas (reutilizável)
  const fetchAssignedCities = async () => {
    try {
      const response = await fetch(`${API_URLS.admincities}/${userId}/cities`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error(`Erro ${response.status}: ${response.statusText}`);
      }
      const data: AssignedCity[] = await response.json();
      setAssignedCitiesDetail(data);
      return data;
    } catch (error: any) {
      console.error("Erro ao buscar cidades atribuídas:", error);
      setError("Erro ao buscar cidades atribuídas.");
      setSnackbarMessage(`Erro ao buscar cidades atribuídas: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return [];
    }
  };

  // 2. Fetch Assigned Cities for the Admin on component mount/userId change
  useEffect(() => {
    if (token && userId) {
      fetchAssignedCities();
    }
    setCitySelections([
      { stateSigla: "", cityCodigoIBGE: null, cityNome: null },
    ]);
    setCitiesForSelection([[]]);
  }, [userId, token]);

  const fetchCities = async (stateSigla: string, index: number) => {
    if (!stateSigla) {
      setCitiesForSelection((prev) => {
        const newArr = [...prev];
        newArr[index] = [];
        return newArr;
      });
      return;
    }
    try {
      const response = await fetch(`${API_IBGE.cidades}/${stateSigla}`);
      const data: CityIBGE[] = await response.json();
      setCitiesForSelection((prev) => {
        const newArr = [...prev];
        newArr[index] = data.sort((a, b) => a.nome.localeCompare(b.nome));
        return newArr;
      });
    } catch (err) {
      setError(`Erro ao carregar cidades para o estado ${stateSigla}.`);
      console.error(`Erro ao carregar cidades para ${stateSigla}:`, err);
    }
  };

  const handleStateChange = (
    event: SelectChangeEvent<string>,
    index: number
  ) => {
    const sigla = event.target.value;
    setCitySelections((prev) => {
      const newSelections = [...prev];
      newSelections[index] = {
        ...newSelections[index],
        stateSigla: sigla,
        cityCodigoIBGE: null,
        cityNome: null,
      };
      return newSelections;
    });
    fetchCities(sigla, index);
  };

  const handleCityChange = (
    event: SelectChangeEvent<string>,
    index: number
  ) => {
    const selectedCodigoIBGE = event.target.value;
    const ibgeCity = citiesForSelection[index].find(
      (city) => city.codigo_ibge === selectedCodigoIBGE
    );

    setCitySelections((prev) => {
      const newSelections = [...prev];
      newSelections[index] = {
        ...newSelections[index],
        cityCodigoIBGE: selectedCodigoIBGE,
        cityNome: ibgeCity ? ibgeCity.nome : null,
      };
      return newSelections;
    });
  };

  const handleAddSelection = () => {
    setCitySelections((prev) => [
      ...prev,
      { stateSigla: "", cityCodigoIBGE: null, cityNome: null },
    ]);
    setCitiesForSelection((prev) => [...prev, []]);
  };

  const handleConfirmDeleteAssignedCity = (
    cityId: string,
    cityName: string
  ) => {
    setCityToRemoveIdConfirm(cityId);
    setCityToRemoveNameConfirm(cityName);
    setOpenConfirmDialog(true);
  };

  const executeDeleteAssignedCity = async () => {
    if (!cityToRemoveIdConfirm || !userId) return;

    setOpenConfirmDialog(false);
    setLoadingDelete(true);

    try {
      const response = await fetch(
        `${API_URLS.admincities}/${userId}/cities/${cityToRemoveIdConfirm}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Erro ao remover cidade.");
      }

      setAssignedCitiesDetail((prev) =>
        prev.filter((city) => city.id !== cityToRemoveIdConfirm)
      );

      setSnackbarMessage("Cidade removida com sucesso!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error: any) {
      console.error("Erro ao remover cidade da API:", error);
      setSnackbarMessage(`Erro ao remover cidade: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoadingDelete(false);
      setCityToRemoveIdConfirm(null);
      setCityToRemoveNameConfirm(null);
    }
  };

  const handleRemoveSelectionLocal = (index: number) => {
    if (
      citySelections.length === 1 &&
      citySelections[0].cityCodigoIBGE === null
    ) {
      return;
    }
    setCitySelections((prev) => prev.filter((_, i) => i !== index));
    setCitiesForSelection((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
    setCityToRemoveIdConfirm(null);
    setCityToRemoveNameConfirm(null);
  };

  const handleSave = () => {
    const newSelectedCityIBGEIds = citySelections
      .filter((s) => s.cityCodigoIBGE !== null)
      .map((s) => parseInt(s.cityCodigoIBGE as string, 10));

    const existingAssignedIds = assignedCitiesDetail.map((city) =>
      parseInt(city.id, 10)
    );

    const allCityIds = Array.from(
      new Set([...existingAssignedIds, ...newSelectedCityIBGEIds])
    );

    onSave(userId, allCityIds);
  };

  return (
    <Box sx={{ mt: 2 }}>
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {/* --- Seção de Cidades Associadas (Tags) --- */}
      <Typography variant="h6" sx={{ mb: 1 }}>
        Cidades Atribuídas:
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          mb: 3,
          minHeight: "40px",
        }}
      >
        {assignedCitiesDetail.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nenhuma cidade atribuída ainda.
          </Typography>
        ) : (
          assignedCitiesDetail.map((city) => {
            const stateId = city.id.substring(0, 2); // Pega os dois primeiros dígitos
            const stateSigla = stateIdToSiglaMap.get(stateId) || "UF"; // Busca a sigla, default "UF"
            return (
              <Chip
                key={city.id}
                label={`[${stateSigla}] ${city.name}`}
                onDelete={() =>
                  handleConfirmDeleteAssignedCity(city.id, city.name)
                }
                deleteIcon={
                  loadingDelete && cityToRemoveIdConfirm === city.id ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <CancelIcon />
                  )
                }
                color="primary"
                variant="outlined"
                disabled={loadingDelete}
              />
            );
          })
        )}
      </Box>
      <hr />

      {/* --- Seção de Adicionar Novas Cidades --- */}
      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
        Adicionar Novas Cidades:
      </Typography>
      {citySelections.map((selection, index) => (
        <Box
          key={index}
          sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}
        >
          <FormControl fullWidth variant="outlined">
            <InputLabel>Estado</InputLabel>
            <Select
              value={selection.stateSigla}
              onChange={(e) => handleStateChange(e, index)}
              label="Estado"
            >
              <MenuItem value="">
                <em>Nenhum</em>
              </MenuItem>
              {states.map((state) => (
                <MenuItem key={state.id} value={state.sigla}>
                  {state.nome}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl
            fullWidth
            variant="outlined"
            disabled={!selection.stateSigla}
          >
            <InputLabel>Cidade</InputLabel>
            <Select
              value={selection.cityCodigoIBGE || ""}
              onChange={(e) => handleCityChange(e, index)}
              label="Cidade"
            >
              <MenuItem value="">
                <em>Nenhum</em>
              </MenuItem>
              {citiesForSelection[index]?.map((city) => (
                <MenuItem key={city.codigo_ibge} value={city.codigo_ibge}>
                  {city.nome}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {citySelections.length > 1 && (
            <IconButton
              onClick={() => handleRemoveSelectionLocal(index)}
              color="error"
            >
              <RemoveCircleOutlineIcon />
            </IconButton>
          )}
        </Box>
      ))}

      <Button
        startIcon={<AddIcon />}
        onClick={handleAddSelection}
        variant="outlined"
        sx={{ mt: 2 }}
        disabled={loadingDelete}
      >
        Adicionar Mais Cidades
      </Button>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, gap: 1 }}>
        <Button
          onClick={onClose}
          color="secondary"
          variant="outlined"
          disabled={loadingDelete}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={
            (citySelections.every((s) => s.cityCodigoIBGE === null) &&
              assignedCitiesDetail.length === 0) ||
            loadingDelete
          }
        >
          Salvar Cidades
        </Button>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Dialog
        open={openConfirmDialog}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">Confirmar Remoção</DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            Tem certeza que deseja remover a cidade **{cityToRemoveNameConfirm}
            **? Esta ação a desassociará do administrador e não pode ser
            desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseConfirmDialog}
            color="primary"
            disabled={loadingDelete}
          >
            Cancelar
          </Button>
          <Button
            onClick={executeDeleteAssignedCity}
            color="error"
            autoFocus
            disabled={loadingDelete}
          >
            Remover
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AtribuirCidades;
